import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getClientFCTransfers,
  getClientWarehouses,
  getCrossThreshold,
  getRegisteredVATIds,
} from '../../core/api';
import { useAppSelector, useQueriesTyped } from '../../hooks';
import SellerSetupCheckThreshold from './SellerSetupCheckThreshold';
import './SellerSetupCheck.scss';
import { formatDate, formatNumber } from '../../core/helpers';
import { subMonths } from 'date-fns';
import CountryLabel from '../../components/CountryLabel/CountryLabel';
import { Loader } from '../../components/Loader/Loader';
import { NormalizedFCTransfer } from '../SetupCheck/SetupCheck/SetupCheck';
import { Table } from '../../components/Table/Table';
import { IdNumberStatusEnum } from './SellerVATIds/SellerVATIds';
import { useFlags } from 'launchdarkly-react-client-sdk';
import MiniLoader from '../../components/MiniLoader/MiniLoader';
import { useThreshold } from '../../context/threshold-context';

export interface CountryThreshold {
  country: string;
  threshold: { value: number; currency: string };
}

export default function SellerSetupCheck() {
  const { vs2912CpPrototypeV2 } = useFlags();
  const { t } = useTranslation();
  const user = useAppSelector((s) => s.user.user);
  const activeCompanyId = useAppSelector(
    (s) => s.user.activeCompany?.company_id
  );

  const { thresholdByCountries, threshold } = useThreshold();

  const [warehouses, FCTransfers, crossThreshold, vatIds] = useQueriesTyped([
    {
      queryKey: ['SellerWarehouses', user!.Id, activeCompanyId],
      queryFn: () => getClientWarehouses(activeCompanyId),
    },
    {
      queryKey: ['SellerFCTransfers', user!.Id, activeCompanyId],
      queryFn: () => getClientFCTransfers(activeCompanyId),
    },
    {
      queryKey: ['SellerCrossThreshold', user!.Id, activeCompanyId],
      queryFn: () => getCrossThreshold(activeCompanyId),
    },
    {
      queryKey: ['SellerRegisteredVATIds', user!.Id, activeCompanyId],
      queryFn: () => getRegisteredVATIds(activeCompanyId),
    },
  ]);

  const FCTransfersData = FCTransfers.data;

  const normalizedFCTransfers: NormalizedFCTransfer[] = useMemo(() => {
    const items = FCTransfersData || [];

    const countries = [
      ...(FCTransfersData || []).map((item) => item.departure_country),
      ...items.map((item) => item.arrival_country),
    ].filter((value, index, self) => {
      return self.findIndex((v) => v === value) === index;
    });

    return countries.reduce((acc: NormalizedFCTransfer[], country) => {
      const arrivals = items.filter(
        (item) => item.departure_country === country
      );
      const departures = items.filter(
        (item) => item.arrival_country === country
      );

      acc.push({
        country,
        departures: {
          countries: departures
            .filter((value, index, self) => {
              return (
                self.findIndex(
                  (v) => v.departure_country === value.departure_country
                ) === index
              );
            })
            .map((item) => item.departure_country),
          total: departures.reduce((acc, item) => {
            return acc + item.number;
          }, 0),
        },
        arrivals: {
          countries: arrivals
            .filter((value, index, self) => {
              return (
                self.findIndex(
                  (v) => v.arrival_country === value.arrival_country
                ) === index
              );
            })
            .map((item) => item.arrival_country),
          total: arrivals.reduce((acc, item) => {
            return acc + item.number;
          }, 0),
        },
      });

      return acc;
    }, []);
  }, [FCTransfersData]);

  const isLoading =
    warehouses.isLoading || crossThreshold.isLoading || vatIds.isLoading;

  return (
    <div className='Page flex-grow-1'>
      <div className='container'>
        {isLoading ? (
          <Loader />
        ) : (
          <div className='Paper Paper--sm mx-auto pt-3'>
            <div className='px-4 mb-3'>
              <div className='SellerSetupCheck__title fw-900 mb-2'>
                {t('VAT Setup')}
              </div>
              <div className='Text--gray mb-2'>
                {formatDate(new Date(2021, 0, 1), 'MMM yyyy')} —{' '}
                {formatDate(subMonths(new Date(), 1), 'MMM yyyy')}
                {crossThreshold.data?.crossed_oss_threshold_label && (
                  <>
                    &nbsp;•&nbsp;
                    {t('OSS threshold exceeded on *q*').replace(
                      '*q*',
                      crossThreshold.data?.crossed_oss_threshold_label
                    )}
                  </>
                )}
              </div>
              <SellerSetupCheckThreshold
                thresholdByCountry={(thresholdByCountries || []).sort(
                  (a, b) => b.threshold.value - a.threshold.value
                )}
                threshold={threshold}
                // oss={answers?.find((a) => a.question_code === 'use_oss')?.answer}
              />
            </div>
            <div className='px-4 mb-3 border-top SellerSetupCheck__border pt-3'>
              <div className='Text--20 fw-800'>{t('Storing')}</div>
              <div className='mb-2'>
                {t('In each of this countries you must have active VAT ID.')}
              </div>
              {!!warehouses.data?.length ? (
                <>
                  <div className='row mb-2'>
                    <div className='col-4 Text--gray'>{t('Country')}</div>
                    <div className='col Text--gray'>{t('Active since')}</div>
                    <div className='col Text--gray'>{t('Items sold')}</div>
                    <div className='col-2 text-center Text--gray'>
                      {t('VAT ID')}
                    </div>
                  </div>
                  {warehouses.data.map((w) => {
                    // const hasVATId = vatIds.includes(w.country);

                    const vatId = (vatIds.data || []).find(
                      (v) => v.Country.short_name === w.country
                    );

                    return (
                      <div className='row mb-3' key={w.country}>
                        <div className='col-4'>
                          <CountryLabel
                            country={w.country}
                            link={vs2912CpPrototypeV2}
                          />
                        </div>
                        <div className='col'>
                          {formatDate(w.active_from, 'dd.MM.yyy')}
                        </div>
                        <div className='col'>
                          {formatNumber(w.count_of_sold_items)}
                        </div>
                        <div className='col-2 text-center'>
                          {vatId?.vat_number_status !== undefined &&
                            typeof vatId.vat_number_status === 'number' &&
                            t(IdNumberStatusEnum[vatId.vat_number_status])}
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <>{t('No storing')}</>
              )}
            </div>

            {FCTransfers.isLoading ? (
              <MiniLoader className='justify-content-center py-3' />
            ) : (
              !!normalizedFCTransfers.length && (
                <div className='px-4 mb-3 border-top SellerSetupCheck__border pt-3'>
                  <div>
                    <div className='Text--20 mb-1 fw-800'>
                      {t(
                        'Transactions between Amazon warehouses (FC_Transfer)'
                      )}
                    </div>
                    <div className='mb-2'>
                      {t(
                        'For such transactions Amazon automatically applies zero VAT rate.'
                      )}
                    </div>
                    <div className='mb-2'>
                      {t(
                        'However, you can only apply the zero VAT rate only if you have a VAT ID for both departure country and arrival country. Otherwise, you will have to pay tax on each item moved based on the purchase price at the applicable VAT rate.'
                      )}
                    </div>
                    <div>
                      <Table
                        className='mb-3 pb-1 mb-lg-0 Table--SellerSetupCheck'
                        columns={[
                          {
                            label: 'Country',
                            field: 'country',
                            formatter: ({ country }) => (
                              <CountryLabel
                                country={country}
                                link={vs2912CpPrototypeV2}
                              />
                            ),
                            maxWidth: '160px',
                          },
                          {
                            label: 'Departures',
                            field: 'departures',
                            formatter: ({ departures }) => (
                              <div className='d-flex'>
                                <div className='fw-600 me-2'>
                                  <div
                                    style={{
                                      minWidth: 52,
                                    }}
                                  >
                                    {formatNumber(departures.total)}
                                  </div>
                                </div>
                                <div className='d-flex flex-wrap'>
                                  {departures.countries.map((c) => (
                                    <div
                                      key={c}
                                      className='me-2 SetupCheck__FCTransferCountryCol'
                                    >
                                      {t(c)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ),
                          },
                          {
                            label: 'Arrivals',
                            field: 'arrivals',
                            formatter: ({ arrivals }) => (
                              <div className='d-flex'>
                                <div className='fw-600 me-2'>
                                  <div
                                    style={{
                                      minWidth: 52,
                                    }}
                                  >
                                    {formatNumber(arrivals.total)}
                                  </div>
                                </div>
                                <div className='d-flex flex-wrap'>
                                  {arrivals.countries.map((c) => (
                                    <div
                                      key={c}
                                      className='me-2 SetupCheck__FCTransferCountryCol'
                                    >
                                      {t(c)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ),
                          },
                        ]}
                        data={normalizedFCTransfers}
                      />
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}
